import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';
import { getStreamFormat } from '@iconik-io/shared-lib/src/utils/get_stream_format';

import type { ContentType, PlaylistItem } from '../../index.js';
import {
  CompareAwarePlayer,
  type CompareAwarePlayerOptions,
} from '../compare_aware_player/compare_aware_player.js';

import streamStyles from '../media_player/stream_player.module.css';
import styles from '../player.module.css';

const logger = DebugLogger.getInstance('unified_player_image');

const MANIFEST_RETRY_DELAY_MS = 5000;

export type ImagePlayerOptions = CompareAwarePlayerOptions & {
  fetchRequestHeaders?: () => Promise<Record<string, string> | undefined>;
};

export class ImagePlayer extends CompareAwarePlayer {
  declare mediaElement: HTMLImageElement | undefined;

  playerType: ContentType = 'image';

  #options: ImagePlayerOptions;
  #retryTimeoutId: ReturnType<typeof setTimeout> | undefined;
  #objectUrl: string | undefined;
  #notReadyPanel: HTMLElement | undefined;

  constructor(options: ImagePlayerOptions) {
    super(options);
    this.#options = options;
    this.mediaElement = new Image();
    this.mediaElement.classList.add(styles.player, styles.transforming_player);
    // Hide the image until it is loaded as an empty src will cause a broken image border to appear
    this.mediaElement.style.display = 'none';
    this.mediaElement.draggable = false;
    if (this.isComparePlayer) {
      this.mediaElement.classList.add(styles.player_compare);
    }
  }

  async destroy() {
    clearTimeout(this.#retryTimeoutId);
    if (this.#objectUrl) {
      URL.revokeObjectURL(this.#objectUrl);
      this.#objectUrl = undefined;
    }
    this.#notReadyPanel?.remove();
    this.#notReadyPanel = undefined;
    await super.destroy();
    this.mediaElement?.remove();
    this.mediaElement = undefined;
  }

  async load(playlistItem: PlaylistItem) {
    if (!this.mediaElement || !playlistItem.url) {
      return;
    }
    this.store.setLoadState('loading');
    this.mediaElement.onload = this.#handleLoad;
    if (getStreamFormat(playlistItem.url)) {
      await this.#loadThroughManifest(playlistItem.url);
    } else {
      this.mediaElement.src = playlistItem.url;
    }
    if (this.store.transformations.$naturalZoom.get() < 1) {
      this.store.transformations.setNaturalZoom();
    }
  }

  // On a watermarked share an image proxy's URL is the manifest endpoint:
  // it responds 425 until the watermarked file is generated, then returns
  // {url} pointing at a storage-accessor location that needs the
  // Authorization header, which an <img> cannot send, hence the blob fetch
  async #loadThroughManifest(manifestUrl: string): Promise<void> {
    try {
      const headers = await this.#options.fetchRequestHeaders?.();
      const response = await fetch(manifestUrl, { headers });
      if (!this.mediaElement) {
        return;
      }

      if (response.status === 425) {
        this.#showNotReadyMessage();
        this.#retryTimeoutId = setTimeout(() => {
          void this.#loadThroughManifest(manifestUrl);
        }, MANIFEST_RETRY_DELAY_MS);
        return;
      }

      if (!response.ok) {
        throw new Error(
          `Manifest request failed with status ${response.status}`,
        );
      }

      const body: unknown = await response.json();
      const url =
        body && typeof body === 'object' && 'url' in body
          ? body.url
          : undefined;
      if (typeof url !== 'string' || !url) {
        throw new Error('Manifest response did not contain a url');
      }

      const authHeaders = Object.fromEntries(
        Object.entries(headers ?? {}).filter(([key]) =>
          key.toLowerCase().includes('authorization'),
        ),
      );
      const imageResponse = await fetch(url, { headers: authHeaders });
      if (!imageResponse.ok) {
        throw new Error(
          `Image request failed with status ${imageResponse.status}`,
        );
      }

      const blob = await imageResponse.blob();
      if (!this.mediaElement) {
        return;
      }
      this.#objectUrl = URL.createObjectURL(blob);
      this.#hideNotReadyMessage();
      this.mediaElement.src = this.#objectUrl;
    } catch (error) {
      logger.debug('Failed to load image through manifest', { error });
      if (this.mediaElement) {
        this.#hideNotReadyMessage();
        this.store.setLoadState('error');
      }
    }
  }

  #showNotReadyMessage(): void {
    this.store.setLoadState(undefined);

    if (!this.#notReadyPanel) {
      this.#notReadyPanel = document.createElement('div');
      this.#notReadyPanel.className = `${styles.player} ${styles.transforming_player} ${streamStyles.proxy_not_ready_panel}`;

      const heading = document.createElement('h3');
      heading.textContent = 'Preparing media';
      const body = document.createElement('p');
      body.textContent =
        'This content is still being processed. Playback will be available once it is ready.';
      this.#notReadyPanel.append(heading, body);

      (this.mediaWrapper ?? this.containerElement).appendChild(
        this.#notReadyPanel,
      );
    }

    this.#notReadyPanel.style.display = 'flex';
  }

  #hideNotReadyMessage(): void {
    if (this.#notReadyPanel) {
      this.#notReadyPanel.style.display = 'none';
    }
  }

  #handleLoad = () => {
    if (!this.mediaElement) {
      // Handles race conditions if load completes after destroy
      return;
    }
    this.mediaElement.style.display = 'block';
    if (!this.isComparePlayer && this.mediaElement.naturalHeight !== 0) {
      this.store.size.setNaturalSize({
        height: this.mediaElement.naturalHeight,
        width: this.mediaElement.naturalWidth,
      });
    }
    this.store.setLoadState('loaded');
    if (
      this.store.transformations.$zoom.get() === 1 &&
      this.store.transformations.$naturalZoom.get() < 1
    ) {
      this.store.transformations.setNaturalZoom();
    }
  };
}
